import { useId, useMemo, useState, type ReactNode } from 'react'
import type { MarketHoldingRow, MarketHoldingsBreakdown, PricingDetailsContent, PricingDetailsRow, PricingSlabTable } from '../types/pricing.types'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ELITE_PLANS_PRICING_DETAILS_TITLE_ID } from '../constants/domIds'
import { resolveElitePlansPricingDetailsDisplay } from '../elitePlansPricingDetailsDisplay'
import { useElitePrimePricingDetails } from '../hooks/useElitePrimePricingDetails'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import AnimatedAccordion from '../../../components/ui-components/AnimatedAccordion'
import Info from '../../../components/ui-components/Info'
import { InfoIcon } from '../../../icons/rebalance/InfoIcon'
import WarningIcon from '../../../icons/WarningIcon'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { mfLogoBaseUrl } from '../../../env'


type ElitePlansPricingDetailsBottomSheetProps = {
  open: boolean
  template: PricingDetailsContent
  onClose: () => void
}

export function ElitePlansPricingDetailsBottomSheet({
  open,
  template,
  onClose
}: ElitePlansPricingDetailsBottomSheetProps) {
  const { status } = useElitePlansLanding()
  const { data: pricingDetails, isLoading, isError, refetch } = useElitePrimePricingDetails(open)
  const content = useMemo(
    () =>
      resolveElitePlansPricingDetailsDisplay({
        template,
        pricingDetails,
        status
      }),
    [template, pricingDetails, status]
  )

  // Pricing API failed (after query retry) — error state instead of template placeholder rows
  if (isError) {
    return <ElitePlansPricingDetailsErrorState onRetry={() => void refetch()} onClose={onClose} />
  }

  return (
    <ElitePlansPricingDetailsBottomSheetContent
      content={content}
      isLoading={isLoading}
      onClose={onClose}
    />
  )
}

function ElitePlansPricingDetailsBottomSheetContent({
  content,
  isLoading,
  onClose
}: {
  content: PricingDetailsContent
  isLoading: boolean
  onClose: () => void
}) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  const [isMarketExpanded, setIsMarketExpanded] = useState(false)
  const noteIdPrefix = useId()

  const logoSrc = content.logoSrc ?? ''
  const logoAlt = content.logoAlt ?? ''
  const eyebrow = content.eyebrow ?? ''
  const price = content.price ?? ''
  const priceSuffix = content.priceSuffix ?? ''
  const assuranceBadge = content.assuranceBadge ?? ''
  const rows = content.rows ?? []
  const lockNote = content.lockNote ?? ''
  const marketEyebrow = content.marketEyebrow ?? ''
  const marketPrice = content.marketPrice ?? ''
  const marketPriceSuffix = content.marketPriceSuffix ?? ''
  const marketBadge = content.marketBadge ?? ''
  const marketBreakdown = content.marketBreakdown
  const showMarketPricing = Boolean(marketPrice)

  return (
    <div className='bg-white text-left'>
      <div className="bg-[url('/images/elite-plans/elite-prime/bottomSheet-background.png')] bg-[length:100%_auto] bg-right-top bg-no-repeat px-5 pb-6 pt-8">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={logoAlt}
            width={72}
            height={72}
            className='mb-4 h-18 w-18 object-contain'
          />
        ) : null}

        <p className='text-xs font-medium uppercase tracking-[1px] text-prime50'>
          {eyebrow}
        </p>
        <h2
          id={ELITE_PLANS_PRICING_DETAILS_TITLE_ID}
          tabIndex={-1}
          className='mt-2 text-[28px] font-medium leading-[1.2] text-black outline-none'
        >
          {price}
          <span className='text-[18px] font-medium text-grey10'>{priceSuffix}</span>
        </h2>

        {assuranceBadge ? (
          <p className='mt-3 inline-flex rounded-[8px] bg-gradient-to-r from-prime10 to-white px-3 py-1.5 text-xs font-medium leading-[1.3] text-prime50'>
            {assuranceBadge}
          </p>
        ) : null}

        {rows.length > 0 ? (
          <ul
            className={`mt-6 list-none overflow-hidden rounded-2xl bg-white border-[#EBF2F2] py-4 ${isLoading ? 'animate-pulse opacity-70' : 'border border-grey80'}`}
            aria-busy={isLoading}
          >
            {rows.map((row) => (
              <ElitePlansPricingDetailsRow
                key={row.id}
                row={row}
                isExpanded={expandedRowId === row.id}
                noteId={`${noteIdPrefix}-${row.id}`}
                onToggle={() =>
                  setExpandedRowId((current) => (current === row.id ? null : row.id))
                }
              />
            ))}
          </ul>
        ) : null}

        {lockNote ? (
          <div className='mt-3 rounded-2xl border border-grey80 bg-white px-4 py-4'>
            <Info lineClassName='!bg-[#D1D6D6]' contentClassName='!text-[13px] !leading-[1.45] !text-grey40'>
              {lockNote}
            </Info>
          </div>
        ) : null}
      </div>

      {showMarketPricing ? (
        <div className='border-t px-5 pb-2 pt-5'>
          <p className='text-xs font-medium uppercase tracking-[1px] text-grey50'>
            {marketEyebrow}
          </p>
          <button
            type='button'
            className='mt-2 flex w-full items-center justify-between gap-3 border-none bg-transparent p-0 text-left'
            aria-expanded={isMarketExpanded}
            aria-controls='elite-plans-market-breakdown'
            onClick={() => setIsMarketExpanded((open) => !open)}
          >
            <span className='text-lg font-medium leading-[1.2] text-black'>
              {marketPrice}
              <span className='text-base font-medium text-grey50'>{marketPriceSuffix}</span>
            </span>
            <ChevronRightIcon
              size={16}
              color='#8E9393'
              className={`shrink-0 transition-transform duration-200 ${isMarketExpanded ? '-rotate-90' : 'rotate-90'}`}
            />
          </button>
          {marketBadge ? (
            <p className='mt-3 inline-flex rounded-[8px] bg-[linear-gradient(90deg,#F2F3F4_0%,#FFF_92.22%)] px-3 py-1.5 text-13 font-medium leading-[1.3] text-grey20'>
              {marketBadge}
            </p>
          ) : null}
          {marketBreakdown ? (
            <AnimatedAccordion isExpanded={isMarketExpanded}>
              <div
                id='elite-plans-market-breakdown'
                className={isLoading ? 'animate-pulse opacity-70' : ''}
              >
                <ElitePlansMarketHoldingsBreakdown breakdown={marketBreakdown} />
              </div>
            </AnimatedAccordion>
          ) : null}
        </div>
      ) : null}

      <div
        className='sticky bottom-0 bg-white px-5 pt-4'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}
      >
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-2xl border-none bg-grey10 px-5 text-[15px] font-medium text-white transition-[filter] duration-150 hover:brightness-110 active:brightness-125'
          onClick={() => {
            trackElitePlansClick(ElitePlansEventId.PricingDetailsClose)
            onClose()
          }}
        >
          Close
        </button>
      </div>
    </div>
  )
}

function ElitePlansPricingDetailsErrorState({
  onRetry,
  onClose
}: {
  onRetry: () => void
  onClose: () => void
}) {
  return (
    <div className='bg-white text-left' role='alert'>
      <div className='px-5 pb-6 pt-8'>
        <WarningIcon className='mb-2' />
        {/* Keeps the dialog's aria-labelledby target so the sheet stays named and focusable */}
        <h2
          id={ELITE_PLANS_PRICING_DETAILS_TITLE_ID}
          tabIndex={-1}
          className='text-2xl font-medium leading-[1] text-black outline-none mb-3'
        >
          Something went wrong
        </h2>
        <p className='max-w-72 text-[14px] text-grey40'>
          We're trying to figure out this issue. Please wait for a few minutes and try again.
        </p>
      </div>

      <div
        className='flex flex-col gap-3 bg-white px-5 pt-4'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}
      >
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-2xl border-none bg-grey10 px-5 text-[15px] font-medium text-white transition-[filter] duration-150 hover:brightness-110 active:brightness-125'
          onClick={onRetry}
        >
          Retry
        </button>
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-2xl border border-[#D1D6D6] bg-white px-5 text-[15px] font-medium text-[#232323]'
          onClick={() => {
            trackElitePlansClick(ElitePlansEventId.PricingDetailsClose)
            onClose()
          }}
        >
          Close
        </button>
      </div>
    </div>
  )
}

export function ElitePlansPricingDetailsRow({
  row,
  isExpanded,
  noteId,
  onToggle
}: {
  row: PricingDetailsRow
  isExpanded: boolean
  noteId: string
  onToggle: () => void
}) {
  const label = row.label ?? ''
  const value = row.value ?? ''
  const note = row.note ?? ''
  const slabTable = row.slabTable
  const showInfo = Boolean(row.showInfo)
  const canExpand = Boolean((row.showChevron || showInfo) && (note || slabTable))
  const valueClassName = row.valueMuted
    ? 'text-[14px] font-medium text-grey40'
    : 'text-[14px] font-medium text-[#232323]'

  let rowBody: ReactNode
  if (!canExpand) {
    rowBody = (
      <div className='flex items-center justify-between gap-3 px-4 py-1'>
        <span className='text-[13px] font-normal text-grey20'>{label}</span>
        <span className={valueClassName}>{value}</span>
      </div>
    )
  } else if (slabTable) {
    // Same markup open/closed so the card chrome, chevron and table height can transition together
    rowBody = (
      <div
        className={`overflow-hidden rounded-2xl transition-[margin,background-color] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isExpanded ? 'mx-2 my-2 bg-grey90' : 'mx-0 my-0 bg-transparent'
          }`}
      >
        <button
          type='button'
          className={`ep-focus-ring flex w-full items-center justify-between gap-3 border-none bg-transparent text-left transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isExpanded ? 'px-3.5 py-3.5' : 'px-4 py-1'
            }`}
          aria-expanded={isExpanded}
          aria-controls={noteId}
          onClick={() => {
            const nextExpanded = !isExpanded
            trackElitePlansClick(ElitePlansEventId.SlabRateToggle, {
              rowId: row.id,
              expanded: nextExpanded
            })
            onToggle()
          }}
        >
          <span
            className={`flex items-center gap-1 text-[13px] ${isExpanded ? 'font-medium text-[#232323]' : 'font-normal text-grey20'
              }`}
          >
            {label}
            <ChevronRightIcon
              size={12}
              color='#232323'
              className={`transition-transform duration-[250ms] ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
            />
          </span>
          <span className={valueClassName}>{value}</span>
        </button>
        <AnimatedAccordion isExpanded={isExpanded}>
          <div id={noteId} className='px-2.5 pb-3'>
            <ElitePlansPricingSlabTable table={slabTable} />
          </div>
        </AnimatedAccordion>
      </div>
    )
  } else if (showInfo) {
    rowBody = (
      <>
        <div className='flex w-full items-center justify-between gap-3 px-4'>
          <span className='flex min-w-0 items-center gap-1 text-[13px] font-normal text-grey40'>
            {label}
            <button
              type='button'
              className='inline-flex shrink-0 border-none bg-transparent p-0 text-grey40'
              aria-label={`About ${label}`}
              aria-expanded={isExpanded}
              aria-controls={noteId}
              onClick={onToggle}
            >
              <InfoIcon width={14} height={14} stroke='currentColor' />
            </button>
          </span>
          <span className={valueClassName}>{value}</span>
        </div>
        {isExpanded && note ? (
          <p id={noteId} className='px-4 pb-3.5 text-xs leading-[1.4] text-grey40'>
            {note}
          </p>
        ) : null}
      </>
    )
  } else {
    rowBody = (
      <>
        <button
          type='button'
          className='ep-focus-ring flex w-full items-center justify-between gap-3 border-none bg-transparent px-4 text-left !py-1'
          aria-expanded={isExpanded}
          aria-controls={noteId}
          onClick={onToggle}
        >
          <span className='flex items-center gap-1 text-[13px] font-normal text-grey20'>
            {label}
            <ChevronRightIcon
              size={12}
              color='#2D2D2D'
              className={`mt-1 transition-transform duration-200 ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
            />
          </span>
          <span className={valueClassName}>{value}</span>
        </button>
        {isExpanded && note ? (
          <p id={noteId} className='px-4 pb-3.5 text-xs leading-[1.4] text-grey40'>
            {note}
          </p>
        ) : null}
      </>
    )
  }

  return (
    <li>
      {row.showDividerBefore ? (
        <div className='mx-4 my-2 border-t border-[#EBF2F2]' aria-hidden='true' />
      ) : null}
      {rowBody}
    </li>
  )
}

export function ElitePlansPricingSlabTable({ table }: { table: PricingSlabTable }) {
  const tiers = table.tiers ?? []
  const portfolioValueLabel = table.portfolioValueLabel ?? ''
  const primeRateLabel = table.primeRateLabel ?? ''
  const maxFeeLabel = table.maxFeeLabel ?? ''
  const roundedPricingLabel = table.roundedPricingLabel ?? ''
  const roundedPricingValue = table.roundedPricingValue ?? ''

  return (
    <div>
      <table className='w-full border-collapse text-xs'>
        <caption className='sr-only'>Elite Prime slab rates by portfolio value</caption>
        <thead>
          <tr className='text-grey40 text-13'>
            <th scope='col' className='pb-2 pl-2 pr-1 text-left font-normal'>{portfolioValueLabel}</th>
            <th scope='col' className='px-1 pb-2 text-center font-normal'>{primeRateLabel}</th>
            <th scope='col' className='pb-2 pl-1 pr-2 text-right font-normal'>{maxFeeLabel}</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier) => {
            const isActive = tier.isActive ?? false
            return (
              <tr
                key={tier.id}
                aria-current={isActive ? 'true' : undefined}
                className={isActive ? 'bg-[rgba(245,212,201,0.30)] text-13 font-medium text-prime50' : 'font-normal text-13 text-grey20'}
              >
                <td
                  className={`py-2.5 pl-2 pr-1 text-left ${isActive ? 'rounded-l-lg border-l-[3px] border-prime50' : ''
                    }`}
                >
                  {tier.portfolioValue ?? ''}
                </td>
                <td className='px-1 py-2.5 text-center'>{tier.primeRate ?? ''}</td>
                <td
                  className={`py-2.5 pl-1 pr-2 text-right ${isActive ? 'rounded-r-lg' : ''
                    }`}
                >
                  {tier.maxFee ?? ''}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {/* {roundedPricingValue ? (
        <div className='mt-2 flex items-center justify-between gap-3 border-t border-[#EBF2F2] px-2 pt-3'>
          <span className='text-[13px] font-normal text-grey40'>{roundedPricingLabel}</span>
          <span className='text-[14px] font-medium text-[#232323]'>{roundedPricingValue}</span>
        </div>
      ) : null} */}
    </div>
  )
}

function resolveFundLogoSrc(logo?: string): string {
  if (!logo) {
    return ''
  }
  if (logo.startsWith('http://') || logo.startsWith('https://')) {
    return logo
  }
  return `${mfLogoBaseUrl}${logo.startsWith('/') ? logo : `/${logo}`}`
}

const MARKET_HOLDINGS_PREVIEW_COUNT = 5
const MARKET_HOLDINGS_EXPAND_THRESHOLD = 6

export function ElitePlansMarketHoldingsBreakdown({
  breakdown
}: {
  breakdown: MarketHoldingsBreakdown
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const note = breakdown?.note ?? ''
  const holdingsLabel = breakdown?.holdingsLabel ?? ''
  const chargesLabel = breakdown?.chargesLabel ?? ''
  const rows = breakdown?.rows ?? []
  const totalLabel = breakdown?.totalLabel ?? ''
  const totalValue = breakdown?.totalValue ?? ''
  // More than six funds: preview five rows and offer expand; six or fewer: show all
  const shouldTruncate = rows.length > MARKET_HOLDINGS_EXPAND_THRESHOLD
  const previewRows = shouldTruncate ? rows.slice(0, MARKET_HOLDINGS_PREVIEW_COUNT) : rows
  const remainingRows = shouldTruncate ? rows.slice(MARKET_HOLDINGS_PREVIEW_COUNT) : []
  const showViewAll = shouldTruncate && !isExpanded

  return (
    <div className='mt-4 bg-white'>
      {note ? (
        <div className='rounded-2xl border border-[#EBF2F2] bg-grey90 px-4 py-4'>
          <Info lineClassName='!bg-grey50' contentClassName='!text-[13px] !leading-[1.45] !text-grey20'>
            {note}
          </Info>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className='mt-4 overflow-hidden rounded-2xl border border-[#EBF2F2] bg-white'>
          <table className='w-full border-collapse'>
            <caption className='sr-only'>Additional charges on your portfolio holdings via regular funds</caption>
            <thead>
              <tr className='border-b border-[#EBF2F2]'>
                <th scope='col' className='px-4 py-3 text-left text-13 font-normal text-[#8E9393]'>
                  {holdingsLabel}
                </th>
                <th scope='col' className='px-4 py-3 text-right text-13 font-normal text-[#8E9393]'>
                  {chargesLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rowIndex) => (
                <MarketHoldingsTableRow
                  key={row.id}
                  row={row}
                  withBottomBorder={!(showViewAll && rowIndex === previewRows.length - 1)}
                />
              ))}
            </tbody>
          </table>

          {shouldTruncate ? (
            <AnimatedAccordion isExpanded={isExpanded}>
              <table className='w-full border-collapse'>
                <tbody>
                  {remainingRows.map((row) => (
                    <MarketHoldingsTableRow key={row.id} row={row} withBottomBorder />
                  ))}
                </tbody>
              </table>
            </AnimatedAccordion>
          ) : null}

          {showViewAll ? (
            <div className='border-b border-[#F2F5F5] px-4 py-3.5 text-center'>
              <button
                type='button'
                className='ep-focus-ring text-13 font-medium text-[#1470CC]'
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(true)}
              >
                {`View all ${rows.length} funds`}
              </button>
            </div>
          ) : null}

          <table className='w-full border-collapse'>
            <tfoot>
              <tr className='bg-[#F7F9FA]'>
                <th scope='row' className='px-4 py-3 text-left text-[13px] font-normal text-grey40'>
                  {totalLabel}
                </th>
                <td className='px-4 py-3 text-right text-[14px] font-medium text-[#232323]'>
                  {totalValue}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}
    </div>
  )
}

function MarketHoldingsTableRow({
  row,
  withBottomBorder
}: {
  row: MarketHoldingRow
  withBottomBorder: boolean
}) {
  return (
    <tr className={withBottomBorder ? 'border-b border-[#F2F5F5]' : undefined}>
      <td className='px-4 py-3.5'>
        <span className='flex items-center gap-3'>
          <img
            src={resolveFundLogoSrc(row.logoSrc)}
            alt=''
            width={32}
            height={32}
            className='h-8 w-8 shrink-0 rounded-lg border border-[#EBF2F2] object-contain p-0.5'
          />
          <span className='flex flex-col'>
            <span className='text-13 font-normal leading-[1.35] text-[#3D3D3D]'>
              {row.fundName ?? ''}
            </span>
            <span className='mt-0.5 text-13 text-grey40'>
              {row.portfolioValue ?? ''}
            </span>
          </span>
        </span>
      </td>
      <td className='px-4 py-3.5 text-right align-middle'>
        <span className='block text-13 font-medium text-[#3D3D3D]'>
          {row.charge ?? ''}
        </span>
        <span className='mt-0.5 block text-13 text-grey40'>
          {row.chargePercent ?? ''}
        </span>
      </td>
    </tr>
  )
}

export const elitePlansPricingDetailsTitleId = ELITE_PLANS_PRICING_DETAILS_TITLE_ID
